/**
 * Dragging a value along a horizontal track, without stealing a phone's scroll.
 *
 * Two controls in the studio are this: a dial on the audio desk, and the bar that
 * chooses which minute a cover film opens on. The gesture rules they need are not
 * obvious and were learned the hard way — the first version of the dial claimed
 * every gesture outright, so on a phone the vertical swipe that is the only way to
 * reach the control *below* it changed a value instead of scrolling the panel.
 * They live here once, so the second control cannot make the mistake again:
 *
 *  - a **press** on the track does not move the value; a **tap** (a press that did
 *    not travel) jumps to where it landed, which is the affordance the old control
 *    was reaching for;
 *  - a **drag** adjusts relative to the value it started on, so grabbing the thumb
 *    does not teleport it;
 *  - the surrounding scroller may take the gesture back at any point, and a swipe
 *    that travelled vertically never settles as a tap;
 *  - on the web the vertical axis is handed to the compositor outright (see
 *    `TRACK_TOUCH_AXIS`), because `PanResponder` otherwise blocks page scrolling
 *    for as long as it holds a gesture.
 *
 * The caller keeps ownership of how the value is drawn and described; this returns
 * the fraction along the track for the drawing, and the handlers for the gesture.
 */
import {useMemo, useRef} from 'react'
import {
  type GestureResponderHandlers,
  type LayoutChangeEvent,
  PanResponder,
  Platform,
  type ViewStyle,
} from 'react-native'

import {clamp} from '#/lib/flashbookAudioEditor'

/**
 * Let the browser own the vertical axis over a track.
 *
 * `touchAction` is a web style, so it is applied on the web alone; on a device the
 * responder system already hands a vertical pan back to the native `ScrollView`,
 * and an unknown style property there is noise at best.
 */
export const TRACK_TOUCH_AXIS: ViewStyle | null =
  Platform.OS === 'web'
    ? ({touchAction: 'pan-y'} as unknown as ViewStyle)
    : null

/** How far a gesture must travel sideways before it is a drag rather than a tap. */
const DRAG_THRESHOLD_PX = 4

export function useTrackScrub({
  value,
  onChange,
  onDragStart,
  onDragEnd,
  min = 0,
  max = 1,
}: {
  value: number
  onChange: (value: number) => void
  /**
   * A finger has landed on the track, and again when it has gone.
   *
   * The cover trimmer is the caller that needs this: while a creator is dragging
   * the minute, the preview has to *hold still*, or they are choosing a frame out
   * of a moving picture. The audio desk's dial ignores both — a knob has no
   * picture to keep steady.
   */
  onDragStart?: () => void
  onDragEnd?: () => void
  min?: number
  max?: number
}) {
  const widthRef = useRef(0)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const onDragStartRef = useRef(onDragStart)
  onDragStartRef.current = onDragStart
  const onDragEndRef = useRef(onDragEnd)
  onDragEndRef.current = onDragEnd
  const valueRef = useRef(value)
  valueRef.current = value
  /** The value the current drag started from. */
  const startRef = useRef(value)
  /** Where the last press landed, as a fraction of the track. */
  const pressedRef = useRef(0)
  /** Whether the current gesture has travelled far enough to count as a drag. */
  const draggedRef = useRef(false)
  const range = max - min || 1

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: (_event, gesture) =>
          Math.abs(gesture.dx) > 6 &&
          Math.abs(gesture.dx) > Math.abs(gesture.dy),
        onPanResponderTerminationRequest: () => true,
        onPanResponderGrant: event => {
          const fraction =
            widthRef.current > 0
              ? clamp(event.nativeEvent.locationX / widthRef.current, 0, 1)
              : 0
          pressedRef.current = fraction
          startRef.current = valueRef.current
          draggedRef.current = false
          onDragStartRef.current?.()
        },
        onPanResponderMove: (_event, gesture) => {
          if (widthRef.current <= 0) return
          if (Math.abs(gesture.dx) > DRAG_THRESHOLD_PX)
            draggedRef.current = true
          const next =
            startRef.current + (gesture.dx / widthRef.current) * range
          onChangeRef.current(clamp(next, min, max))
        },
        onPanResponderRelease: (_event, gesture) => {
          onDragEndRef.current?.()
          // A movement that went mostly *down* the screen is a scroll the scroller
          // never claimed, not a tap on this track.
          if (draggedRef.current || Math.abs(gesture.dy) > 8) return
          onChangeRef.current(clamp(min + pressedRef.current * range, min, max))
        },
        // The scroller can take a gesture back at any point, and a drag that
        // never reports its end would leave the caller believing a finger is
        // still down — which, for the trimmer, means a film that stays frozen.
        onPanResponderTerminate: () => onDragEndRef.current?.(),
      }),
    [max, min, range],
  )

  return {
    /** Wire to the track's `onLayout`: the gesture is measured in its pixels. */
    onTrackLayout: (event: LayoutChangeEvent) => {
      widthRef.current = event.nativeEvent.layout.width
    },
    panHandlers: panResponder.panHandlers as GestureResponderHandlers,
    touchAxis: TRACK_TOUCH_AXIS,
    fraction: clamp((value - min) / range, 0, 1),
  }
}
